import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import ProductsTab from "@/components/admin/ProductsTab";
import AnnouncementsTab from "@/components/admin/AnnouncementsTab";
import ContentTab from "@/components/admin/ContentTab";
import EnquiriesTab from "@/components/admin/EnquiriesTab";
import { Loader2, LogOut, ArrowLeft } from "lucide-react";

const TABS = [
  { id: "products", label: "Products", testid: "admin-tab-products" },
  { id: "announcements", label: "Announcements", testid: "admin-tab-announcements" },
  { id: "content", label: "Site Content", testid: "admin-tab-content" },
  { id: "enquiries", label: "Enquiries", testid: "admin-tab-enquiries" },
];

const inputCls =
  "w-full bg-white border border-[#263562]/15 rounded-lg px-4 py-3 text-sm text-[#1E2A4A] placeholder:text-[#949CA1] outline-none focus:border-[#3AB0E1] focus:ring-1 focus:ring-[#3AB0E1]/30 transition-all";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("hostech_admin_token"));
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("products");

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("hostech_admin_token", data.token);
      setUser(data.user);
      setToken(data.token);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("hostech_admin_token");
    setToken(null);
    setUser(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F5F8FC] flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/assets/hostech-logo.png" alt="Hostech Solutions" className="h-16 w-auto mx-auto" />
            <h1 className="mt-6 font-display font-semibold text-2xl text-[#263562]">Admin CMS</h1>
            <p className="mt-1 text-sm text-[#5A6B85]">Sign in to manage website content</p>
          </div>
          <form
            onSubmit={login}
            className="bg-white rounded-2xl border border-[#263562]/10 shadow-[0_20px_60px_-20px_rgba(38,53,98,0.2)] p-8"
          >
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mb-2">
              Email
            </label>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@hostechsolutions.co.ke"
              className={inputCls}
            />
            <label className="block font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#949CA1] mt-5 mb-2">
              Password
            </label>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className={inputCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-[#263562] hover:bg-[#3AB0E1] disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full transition-colors duration-300"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <a
            href="/"
            className="mt-6 flex items-center justify-center gap-2 text-sm text-[#5A6B85] hover:text-[#263562] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F8FC]">
      <header className="bg-[#263562] text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-md p-1">
              <img src="/assets/hostech-icon.png" alt="" className="w-7 h-7" />
            </span>
            <div>
              <p className="font-display font-semibold leading-none">Hostech CMS</p>
              <p className="font-mono-tech text-[9px] uppercase tracking-[0.25em] text-[#3AB0E1] mt-0.5">
                Website Administration
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="hidden sm:block text-xs text-slate-300 hover:text-white transition-colors">
              View site ↗
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs font-medium border border-white/25 hover:border-[#3AB0E1] hover:text-[#3AB0E1] rounded-full px-4 py-2 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all duration-300 ${
                tab === t.id
                  ? "bg-[#263562] text-white border-[#263562]"
                  : "bg-white border-[#263562]/15 text-[#5A6B85] hover:border-[#3AB0E1]/60 hover:text-[#263562]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div data-testid="admin-dashboard">
          {tab === "products" && <ProductsTab />}
          {tab === "announcements" && <AnnouncementsTab />}
          {tab === "content" && <ContentTab />}
          {tab === "enquiries" && <EnquiriesTab />}
        </div>
      </div>
    </div>
  );
}
